import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Target, Users, Calendar, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { fetchApi, formatDate } from '../lib/utils';

export default function Challenges() {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);

  useEffect(() => {
    loadChallenges();
  }, []);

  const loadChallenges = async () => {
    try {
      const [available, joined] = await Promise.all([
        fetchApi('/challenges'),
        fetchApi('/challenges/my')
      ]);
      setChallenges(available);
      setMyChallenges(joined);
    } catch (error) {
      console.error('Load challenges error:', error);
    } finally {
      setLoading(false);
    }
  };

  const joinChallenge = async (challengeId) => {
    setJoining(challengeId);
    try {
      await fetchApi(`/challenges/${challengeId}/join`, { method: 'POST' });
      loadChallenges();
    } catch (error) {
      console.error('Join error:', error);
    } finally {
      setJoining(null);
    }
  };

  const isJoined = (challengeId) => myChallenges.some(c => c.id === challengeId);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="text-lg font-bold">Monthly Challenges</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Active Challenges */}
        {myChallenges.length > 0 && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#F59E0B]" />
                Your Active Challenges
              </h2>
              <div className="grid gap-4">
                {myChallenges.map((challenge, index) => (
                  <motion.div
                    key={challenge.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="card-default"
                    data-testid={`my-challenge-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg">{challenge.name}</h3>
                        <p className="text-sm text-zinc-400">{challenge.description}</p>
                      </div>
                      {challenge.completed ? (
                        <div className="px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs font-bold flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Completed
                        </div>
                      ) : (
                        <div className="text-right">
                          <p className="text-2xl font-black text-[#CCFF00]">
                            {Math.round(challenge.user_progress || 0)}%
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">Progress</span>
                        <span>
                          {((challenge.user_progress || 0) / 100 * challenge.target_value).toFixed(1)} / {challenge.target_value} {challenge.unit}
                        </span>
                      </div>
                      <Progress
                        value={challenge.user_progress || 0}
                        className="h-2 bg-zinc-800"
                      />
                    </div>
                    
                    <div className="mt-4 flex items-center gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Ends {formatDate(challenge.end_date)}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </>
        )}

        {/* Available Challenges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#6366F1]" />
            Available Challenges
          </h2>
          
          {challenges.length > 0 ? (
            <div className="grid gap-4">
              {challenges.map((challenge, index) => {
                const joined = isJoined(challenge.id);
                const myChallenge = myChallenges.find(c => c.id === challenge.id);
                
                return (
                  <motion.div
                    key={challenge.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`card-default ${joined ? 'border-[#CCFF00]/30' : ''}`}
                    data-testid={`challenge-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                          <Trophy className="w-6 h-6 text-[#F59E0B]" />
                        </div>
                        <div>
                          <h3 className="font-bold">{challenge.name}</h3>
                          <p className="text-sm text-zinc-400">{challenge.description}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm mb-4">
                      <div className="px-3 py-1 rounded-full bg-zinc-800">
                        <span className="text-zinc-400">Target: </span>
                        <span className="font-bold">{challenge.target_value} {challenge.unit}</span>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-zinc-800 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-zinc-400" />
                        <span>Ends {formatDate(challenge.end_date)}</span>
                      </div>
                    </div>
                    
                    {joined ? (
                      <div className="flex items-center gap-2 text-[#CCFF00]">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Joined</span>
                        {myChallenge && (
                          <span className="ml-auto text-sm">
                            Progress: {Math.round(myChallenge.user_progress || 0)}%
                          </span>
                        )}
                      </div>
                    ) : (
                      <Button
                        onClick={() => joinChallenge(challenge.id)}
                        disabled={joining === challenge.id}
                        className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                        data-testid={`join-${index}-btn`}
                      >
                        {joining === challenge.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Join Challenge'
                        )}
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Active Challenges</h3>
              <p className="text-zinc-400">New challenges are coming soon!</p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
